<template lang="pug">
div
  BriqMultilanguageWarningModal(
    v-if="hasMultipleLanguages"
    v-show="showWarningModal"
    :defaultModel="deal"
    :translationInfo="deal.TranslationInfo"
    :translatableProperties="translatableProperties"
    @close="showWarningModal = false" 
    @onSubmit="save"
    )
  UpsertPageWithTabs(
    :title="deal.Key ? 'Edit deal' : 'Create deal'"
    @save="validateAndSave"
    @cancel="$emit('cancel')"
    :tabNames="tabs"
    :pending="isSaving"
    :width="'1050px'"
    :canSave="!isSaving"
    :showAuditTrail="canViewAuditTrail"
    objectType="ProductGroup",
    :objectKey="deal.Key"
    )
    template(slot="tab-deal")
      DealTab(:deal="deal" :disabled="isSaving")
    template(slot="tab-products & extras")  
      ProductsAndExtrasTab(:deal="deal" :products="products" :pending="isSaving")
    template(slot="tab-online")
      OnlineTab(:deal="deal" :businessUnit="businessUnit" :funnelBaseUrl="funnelBaseUrl" :disabled="isSaving" :enableMultilanguage="enableMultilanguage")
</template>

<script>
import UpsertPageWithTabs from '@v/components/UpsertPageWithTabs.vue'
import DealTab from './tab-deal/DealTab.vue'
import ProductsAndExtrasTab from './tab-products-and-extras/ProductsAndExtrasTab.vue'
import OnlineTab from './tab-online/OnlineTab.vue'
import validateSlug from '@/helpers/validateSlug.js'
import { required, minLength } from 'vuelidate/lib/validators'
import BriqMultilanguageWarningModal from '@/vue/components/BriqMultilanguageWarningModal.vue'
import { emptyTranslatableFieldsExist } from '@v/util/emptyTranslatableFields.js'
import completeTranslationInfo from '@/vue/util/completeTranslationInfo.js'
import {
  isLowerThanMaxPersons,
  dealAvailabilityStartEndTimeValidation,
  dealAvailbilityStartDateIsBeforeOrEqualToEndDate,
} from '@/vue/util/customValidations.js'
import validateBaseProduct from '@/helpers/validateBaseProduct.js'
import { reduce, assoc, pipe, keys } from 'ramda'

export default {
  name: 'UpsertDeal',
  components: {
    UpsertPageWithTabs,
    DealTab,
    OnlineTab,
    BriqMultilanguageWarningModal,
    ProductsAndExtrasTab,
  },
  props: {
    deal: { type: Object, required: true },
    businessUnit: { type: Object, required: true },
    products: { type: Array, required: true },
    funnelBaseUrl: String,
    isSaving: Boolean,
    canViewAuditTrail: Boolean,
    enableMultilanguage: Boolean, // featuer flag
    startOfDay: { type: String, required: true },
  },
  data() {
    return {
      showWarningModal: false,
      tabs: ['Deal', 'Products & Extras', 'Online'],
    }
  },
  validations() {
    const timeValidation = {
      $each: { startEndTimeValidation: dealAvailabilityStartEndTimeValidation(this.startOfDay) },
    }
    const weekDayTimesValidation = this.deal.AvailableWeekdayTimes
      ? pipe(
          keys,
          reduce((a, n) => assoc(n, timeValidation, a), {}),
        )(this.deal.AvailableWeekdayTimes)
      : {}
    return {
      deal: {
        Slug: { validateSlug },
        Products: { required, minLength: minLength(1), validateBaseProduct },
        InternalName: { required },
        MinPersons: { isLowerThanMaxPersons },
        AvailableWeekdayTimes: weekDayTimesValidation,
        AvailabilityStartDate: { dealAvailbilityStartDateIsBeforeOrEqualToEndDate },
      },
    }
  },
  computed: {
    hasMultipleLanguages() {
      return (
        this.enableMultilanguage &&
        !!this.deal.TranslationInfo?.AvailableCultures?.length &&
        this.deal.Key !== null
      )
    },
    translatableProperties() {
      return [
        {
          label: 'Name',
          key: 'ExternalName',
        },
        {
          label: 'Description',
          key: 'Description',
        },
        {
          label: 'What do I get',
          key: 'Usps',
        },
        {
          label: 'Image alt',
          key: 'ImageAltTitle',
        },
        {
          label: 'Program',
          key: 'Program',
        },
      ]
    },
  },
  provide() {
    return {
      validations: this.$v,
      startOfDay: this.startOfDay,
    }
  },
  methods: {
    validateAndSave() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$notifyWarning('Validation failed, please doublecheck all fields')
        return
      }
      if (
        this.hasMultipleLanguages &&
        emptyTranslatableFieldsExist(
          this.deal,
          this.deal.TranslationInfo,
          this.translatableProperties,
        )
      ) {
        this.showWarningModal = true
        return
      }
      this.save()
    },
    save() {
      this.$emit('save', this.deal)
    },
  },
  beforeMount() {
    if (!this.enableMultilanguage) return
    completeTranslationInfo(this.deal)
  },
  mounted() {
    this.$bus.$on('remove-product-from-deal', removedProductKey => {
      this.deal.Products = this.deal.Products.filter(p => p.Key !== removedProductKey)
      this.deal.Upsells = this.deal.Upsells.filter(p => p.Key !== removedProductKey)
    })
  },
}
</script>
